import GenerateExportButton from "@app/components/generate-export-button";
import KpiChart from "@app/components/kpi-chart";
import { useToast } from "@app/components/toast";
import { getAuthToken } from "@app/relay/fetch-relay";
import { Suspense, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { incomeCurvesPage_Query } from "@app/queries/incomeCurvesPage_Query.graphql";
import type { incomeCurvesPage_DetailQuery } from "@app/queries/incomeCurvesPage_DetailQuery.graphql";
import type { incomeCurvesPage_PaybackQuery } from "@app/queries/incomeCurvesPage_PaybackQuery.graphql";
import type { incomeCurvesPage_PeriodsQuery } from "@app/queries/incomeCurvesPage_PeriodsQuery.graphql";

const MONTH_COLORS = [
  "#2563eb", "#16a34a", "#d97706", "#dc2626", "#7c3aed", "#0d9488",
  "#db2777", "#65a30d", "#0891b2", "#9333ea", "#ca8a04", "#e11d48",
];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const CURRENT_YEAR = new Date().getFullYear();

const curvesQuery = graphql`
  query incomeCurvesPage_Query($year: Int!) {
    incomeCurves(year: $year) {
      label
      size
      payingCustomers
      points {
        month
        value
      }
      forecast {
        month
        value
      }
    }
  }
`;

const detailQuery = graphql`
  query incomeCurvesPage_DetailQuery($signupMonth: String!, $tenureMonths: Int!) {
    incomeCohortDetail(signupMonth: $signupMonth, tenureMonths: $tenureMonths) {
      signupMonth
      tenureMonths
      total
      subscriptionTotal
      paygTotal
      invoices {
        stripeId
        daysAfterSignup
        amount
        subscription
      }
    }
  }
`;

const paybackQuery = graphql`
  query incomeCurvesPage_PaybackQuery($year: Int!) {
    incomeCacPayback(year: $year) {
      label
      size
      points {
        month
        value
      }
    }
  }
`;

const periodsQuery = graphql`
  query incomeCurvesPage_PeriodsQuery {
    cacPaybackPeriods {
      signupMonth
      paybackMonths
      forecast
    }
  }
`;

type Selected = { signupMonth: string; tenureMonths: number };

const money0 = (v: number) =>
  v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const money2 = (v: number) =>
  v.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
const monthLabel = (ym: string) => {
  const [yy, mm] = ym.split("-");
  return `${MONTH_NAMES[parseInt(mm, 10) - 1]} ${yy}`;
};

type Point = { readonly month: string; readonly value: number | null | undefined };
type IncomeCurve = {
  readonly label: string;
  readonly payingCustomers: number;
  readonly points: ReadonlyArray<Point>;
  readonly forecast: ReadonlyArray<Point>;
};

const bucketOf = (month: string) => parseInt(month.slice(1), 10);

// Build the chart series: per cohort, a SOLID line for the actual curve and (when
// the cohort is incomplete) a DASHED line for the forecast continuation, same
// colour. Every series is padded to a common tenure axis with nulls outside its
// own range so the lines align (and the solid stops / dashed starts at the
// connector). When "per paying customer" is on, both are divided by the cohort's
// paying-customer count.
function buildIncomeSeries(curves: ReadonlyArray<IncomeCurve>, perCustomer: boolean) {
  let axisMax = 0;
  for (const curve of curves) {
    for (const point of curve.points) axisMax = Math.max(axisMax, bucketOf(point.month));
    for (const point of curve.forecast) axisMax = Math.max(axisMax, bucketOf(point.month));
  }
  const months = Array.from({ length: axisMax + 1 }, (_, n) => `M${n}`);

  return curves.flatMap((curve, i) => {
    const color = MONTH_COLORS[i % MONTH_COLORS.length];
    const divisor = perCustomer && curve.payingCustomers > 0 ? curve.payingCustomers : 1;
    const scaled = (points: ReadonlyArray<Point>) => {
      const byBucket = new Map(points.map((p) => [bucketOf(p.month), p.value]));
      return months.map((m, n) => {
        const v = byBucket.get(n);
        return { month: m, value: byBucket.has(n) && v != null ? v / divisor : null };
      });
    };

    const solid = { name: curve.label, type: "line" as const, color, points: scaled(curve.points) };
    if (curve.forecast.length === 0) return [solid];
    const dashed = { name: `${curve.label} (forecast)`, type: "line" as const, color, dashed: true, points: scaled(curve.forecast) };
    return [solid, dashed];
  });
}

// Queues the full cohort-analysis matrix (60 months x tenure, derived
// server-side) and sends the user to /exports. It used to build inside this
// request and stream back, which left no record once the tab closed.
function ExcelExportButton() {
  return (
    <GenerateExportButton kind="COHORT_ANALYSIS" label="Generate cohort analysis" />
  );
}

// Income curves: per signup-month cohort, cumulative actual revenue day-indexed
// from signup. Left = the year's curves (one per signup month); click any point
// to load the paid invoices behind it into the detail card on the right.
export default function IncomeCurvesPage() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [selected, setSelected] = useState<Selected | null>(null);
  const [perCustomer, setPerCustomer] = useState(false);

  const goYear = (delta: number) => {
    setYear((y) => y + delta);
    setSelected(null);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => goYear(-1)}
            className="text-2xl leading-none text-gray-400 hover:text-gray-800 px-1"
            aria-label="Previous year"
          >
            ‹
          </button>
          <h1 className="text-xl font-bold">
            Income curves <span className="text-blue-600">{year}</span>
          </h1>
          <button
            type="button"
            onClick={() => goYear(1)}
            disabled={year >= CURRENT_YEAR}
            className="text-2xl leading-none text-gray-400 hover:text-gray-800 px-1 disabled:opacity-25 disabled:hover:text-gray-400"
            aria-label="Next year"
          >
            ›
          </button>
        </div>
        <ExcelExportButton />
      </div>
      <p className="text-sm text-gray-500 mt-1 mb-4">
        Cumulative revenue (subscriptions + one-off / PAYG, from paid invoices) generated by each first-payment
        cohort, by calendar month from each customer's first payment (the a16z/Bessemer CAC-payback basis). Click
        any point on a curve to see the invoices behind it.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <Suspense fallback={<div className="bg-white rounded border p-6 text-sm text-gray-400">Loading curves…</div>}>
          <IncomeChart
            year={year}
            onSelect={setSelected}
            perCustomer={perCustomer}
            onTogglePerCustomer={setPerCustomer}
          />
        </Suspense>

        {selected ? (
          <Suspense
            key={`${selected.signupMonth}-${selected.tenureMonths}`}
            fallback={<div className="bg-white rounded border p-6 text-sm text-gray-400">Loading invoices…</div>}
          >
            <DetailCard selected={selected} />
          </Suspense>
        ) : (
          <div className="bg-white rounded border p-6 text-sm text-gray-400">
            Click a point on a curve to see the cumulative revenue and the paid invoices behind it.
          </div>
        )}
      </div>

      <div className="mt-6">
        <Suspense fallback={<div className="bg-white rounded border p-6 text-sm text-gray-400">Loading CAC payback…</div>}>
          <PaybackSection year={year} />
        </Suspense>
      </div>

      <div className="mt-6">
        <Suspense fallback={<div className="bg-white rounded border p-6 text-sm text-gray-400">Loading payback periods…</div>}>
          <PaybackPeriodsSection />
        </Suspense>
      </div>
    </div>
  );
}

// CAC payback PERIOD (months to recover CAC) per signup month over the past 5
// years — a solid line where the cohort actually paid back, a dashed line where
// it's projected from the income forecast (connected at the transition).
function PaybackPeriodsSection() {
  const data = useLazyLoadQuery<incomeCurvesPage_PeriodsQuery>(periodsQuery, {}, { fetchPolicy: "store-or-network" });
  const periods = data.cacPaybackPeriods;

  if (periods.length === 0) {
    return (
      <div className="bg-white rounded border p-6 text-sm text-gray-400">
        No payback periods — needs gross margin + fully-loaded CAC from the P&amp;L.
      </div>
    );
  }

  let lastActual = -1;
  periods.forEach((p, i) => {
    if (!p.forecast && p.paybackMonths != null) lastActual = i;
  });

  const series = [
    {
      name: "Actual",
      type: "line" as const,
      color: "#2563eb",
      points: periods.map((p) => ({ month: p.signupMonth, value: !p.forecast ? p.paybackMonths : null })),
    },
    {
      name: "Forecast",
      type: "line" as const,
      color: "#2563eb",
      dashed: true,
      // forecast points + the last actual point as a connector so the dashes join the solid.
      points: periods.map((p, i) => ({
        month: p.signupMonth,
        value: p.forecast ? p.paybackMonths : i === lastActual ? p.paybackMonths : null,
      })),
    },
  ];

  return (
    <div className="space-y-1">
      <KpiChart
        title="CAC payback period by first-payment cohort — months to recover CAC (past 5 years)"
        series={series}
        format="number"
        formatXLabel={(m) => (m.endsWith("-01") ? m.slice(0, 4) : "")}
        onMonthClick={() => {}}
      />
      <p className="text-[11px] text-gray-400 px-2">
        Each point is one first-payment cohort: months (from first payment) until cumulative gross profit (cohort
        income × blended gross margin) recovers its CAC (blended TTM CAC per paying customer × new paying customers).{" "}
        <span className="text-blue-600">Solid</span> =
        the cohort has actually paid back; <span className="text-blue-600">dashed</span> = projected from the income
        forecast (not yet paid back). Gaps = not recovered within the forecast horizon. Current month excluded.
      </p>
    </div>
  );
}

function PaybackSection({ year }: { year: number }) {
  const data = useLazyLoadQuery<incomeCurvesPage_PaybackQuery>(paybackQuery, { year }, { fetchPolicy: "store-or-network" });
  const curves = data.incomeCacPayback;

  if (curves.length === 0) {
    return (
      <div className="bg-white rounded border p-6 text-sm text-gray-400">
        No CAC payback for {year} — needs fully-loaded S&amp;M (Customer success + Marketing) and a gross margin from the P&amp;L.
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <KpiChart
        title="CAC payback by cohort — share of fully-loaded CAC recovered, by months since first payment"
        series={curves.map((curve, i) => ({
          name: curve.label,
          points: curve.points,
          type: "line" as const,
          color: MONTH_COLORS[i % MONTH_COLORS.length],
        }))}
        format="percent"
        referenceLine={{ value: 1, label: "paid back" }}
        formatXLabel={(m) => `M${parseInt(m.slice(1), 10) + 1}`}
        onMonthClick={() => {}}
      />
      <p className="text-[11px] text-gray-400 px-2">
        Each curve is one first-payment cohort: cumulative gross profit (cohort income × the company's blended gross
        margin) as a share of that cohort's fully-loaded CAC (blended TTM S&amp;M per new paying customer). It crosses
        the 100% line at payback. Independent of the "per paying customer" toggle (a ratio of cohort totals).
      </p>
    </div>
  );
}

function IncomeChart({
  year,
  onSelect,
  perCustomer,
  onTogglePerCustomer,
}: {
  year: number;
  onSelect: (s: Selected) => void;
  perCustomer: boolean;
  onTogglePerCustomer: (v: boolean) => void;
}) {
  const data = useLazyLoadQuery<incomeCurvesPage_Query>(curvesQuery, { year }, { fetchPolicy: "store-or-network" });
  const curves = data.incomeCurves;

  if (curves.length === 0) {
    return <div className="bg-white rounded border p-6 text-sm text-gray-400">No paying cohorts in {year}.</div>;
  }

  const series = buildIncomeSeries(curves, perCustomer);

  return (
    <div className="space-y-1">
      <div className="relative">
        <label className="absolute right-2 top-2 z-10 flex items-center gap-1.5 text-xs bg-white/95 px-2 py-1 rounded border cursor-pointer select-none shadow-sm">
          <input type="checkbox" checked={perCustomer} onChange={(e) => onTogglePerCustomer(e.target.checked)} />
          per paying customer
        </label>
        <KpiChart
          title={
            perCustomer
              ? "Cumulative revenue per paying customer, by tenure month"
              : "Cumulative revenue by tenure month"
          }
          series={series}
          format="money"
          formatXLabel={(m) => `M${parseInt(m.slice(1), 10) + 1}`}
          // Force point symbols: the forecast pushes the x-axis past the auto-thin
          // threshold, and symbols are what make the points click-to-drill.
          showPointSymbols
          onMonthClick={() => {}}
          onPointClick={(seriesName, month) => {
            // Forecast (dashed) points are projections — no invoices to drill.
            if (seriesName.endsWith("(forecast)")) return;
            // month is "M{offset}" (0-based bucket); tenure month = offset + 1.
            onSelect({ signupMonth: seriesName, tenureMonths: parseInt(month.slice(1), 10) + 1 });
          }}
        />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 px-2">
        {curves.map((curve, i) => (
          <span key={curve.label} className="flex items-center gap-1 text-xs text-gray-600">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: MONTH_COLORS[i % MONTH_COLORS.length] }} />
            {MONTH_NAMES[parseInt(curve.label.slice(5), 10) - 1]}
            <span className="text-gray-400">
              {perCustomer ? `${curve.payingCustomers} paying` : `n=${curve.size}`}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

function DetailCard({ selected }: { selected: Selected }) {
  const data = useLazyLoadQuery<incomeCurvesPage_DetailQuery>(detailQuery, selected, { fetchPolicy: "store-or-network" });
  const detail = data.incomeCohortDetail;

  return (
    <div className="bg-white rounded border p-4 space-y-3">
      <p className="text-sm text-gray-800">
        Customers who first paid in <strong>{monthLabel(detail.signupMonth)}</strong> generated a total of{" "}
        <strong className="text-green-700">{money0(detail.total)}</strong> within their first {detail.tenureMonths}{" "}
        month{detail.tenureMonths === 1 ? "" : "s"}.
      </p>
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span>
          Subscription: <strong className="text-gray-700">{money0(detail.subscriptionTotal)}</strong>
        </span>
        <span>
          One-off / PAYG: <strong className="text-gray-700">{money0(detail.paygTotal)}</strong>
        </span>
        <span className="ml-auto">{detail.invoices.length} invoices</span>
      </div>

      <div className="overflow-auto max-h-[30rem] border rounded">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-gray-50 text-gray-500">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Days after first payment</th>
              <th className="px-2 py-1.5 text-left font-medium">Invoice (Stripe id)</th>
              <th className="px-2 py-1.5 text-left font-medium">Type</th>
              <th className="px-2 py-1.5 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {detail.invoices.length === 0 && (
              <tr>
                <td colSpan={4} className="px-2 py-4 text-gray-400">
                  No paid invoices in this window.
                </td>
              </tr>
            )}
            {detail.invoices.map((inv) => (
              <tr key={inv.stripeId} className="hover:bg-gray-50">
                <td className="px-2 py-1.5 tabular-nums text-gray-600 whitespace-nowrap">+{inv.daysAfterSignup}d</td>
                <td className="px-2 py-1.5 font-mono text-gray-500">{inv.stripeId}</td>
                <td className="px-2 py-1.5">
                  <span className={inv.subscription ? "text-blue-600" : "text-amber-600"}>
                    {inv.subscription ? "subscription" : "one-off / PAYG"}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{money2(inv.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
